import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Collapse,
  IconButton,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

import { useCrossScraperKeywords } from '../../../hooks/useCrossScraperKeywords';

const ACTION = {
  NONE: 'none',
  RENAME: 'rename',
  DELETE_SELECTED: 'delete_selected',
  DELETE_ALL: 'delete_all',
};

export default function CrossScraperKeywordsDialog({
  open,
  onClose,
  onMutationComplete,
}) {
  const theme = useTheme();
  const {
    searchResults,
    searchLoading,
    actionLoading,
    error,
    setError,
    searchScrapers,
    massDelete,
    massEdit,
    clearSearch,
  } = useCrossScraperKeywords();

  const [searchInput, setSearchInput] = useState('');
  const [partialMatch, setPartialMatch] = useState(false);
  const [activeAction, setActiveAction] = useState(ACTION.NONE);
  const [newKeyword, setNewKeyword] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleClose = () => {
    setSearchInput('');
    setPartialMatch(false);
    setActiveAction(ACTION.NONE);
    setNewKeyword('');
    setSelectedIds(new Set());
    clearSearch();
    onClose();
  };

  const handleSearch = useCallback(async () => {
    if (!searchInput.trim()) return;
    setActiveAction(ACTION.NONE);
    setSelectedIds(new Set());
    try {
      await searchScrapers(searchInput.trim(), partialMatch);
    } catch { /* error state handled by hook */ }
  }, [searchInput, partialMatch, searchScrapers]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const refreshSearch = useCallback(async () => {
    if (!searchInput.trim()) return;
    try {
      await searchScrapers(searchInput.trim(), partialMatch);
    } catch { /* handled by hook */ }
  }, [searchInput, partialMatch, searchScrapers]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const toggleAction = (action) => {
    setActiveAction((prev) => (prev === action ? ACTION.NONE : action));
    setNewKeyword('');
  };

  const toggleRowSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (ids, checked) => {
    setSelectedIds(checked ? new Set(ids) : new Set());
  };

  // Both "Delete Selected" and "Delete from All" delete by keyword_id — the exact
  // rows the search returned — so the deleted count always matches what the user
  // saw, regardless of case or partial-match differences in the search term.
  const runMassDelete = async (keywordIds) => {
    if (!keywordIds.length) return;
    try {
      const result = await massDelete({ keywordIds });
      showSnackbar(
        `Deleted from ${result.deleted_count} scraper(s): ${result.project_names_affected?.join(', ') || 'none'}`,
      );
      setActiveAction(ACTION.NONE);
      setSelectedIds(new Set());
      await refreshSearch();
      onMutationComplete?.();
    } catch { /* error shown via hook */ }
  };

  const handleDeleteSelected = () => runMassDelete([...selectedIds]);
  const handleDeleteAll = () =>
    runMassDelete((searchResults?.scrapers || []).map((s) => s.keyword_id));

  // Rename by keyword_id — the exact rows the search returned — so the renamed
  // count always matches what the user saw, regardless of case or partial-match
  // differences in the search term.
  const handleMassEdit = async () => {
    const keywordIds = (searchResults?.scrapers || []).map((s) => s.keyword_id);
    if (!keywordIds.length || !newKeyword.trim()) return;
    if (newKeyword.trim() === searchInput.trim()) {
      setError('New keyword must differ from the current keyword');
      return;
    }
    try {
      const result = await massEdit({ keywordIds, newKeyword: newKeyword.trim() });
      const parts = [`Renamed in ${result.updated_count} scraper(s)`];
      if (result.skipped?.length) parts.push(`Skipped: ${result.skipped.join(', ')}`);
      if (result.removed_duplicate_count) {
        parts.push(`Removed ${result.removed_duplicate_count} duplicate row(s)`);
      }
      if (result.errors?.length) parts.push(`${result.errors.length} error(s)`);
      showSnackbar(parts.join('. '), result.errors?.length ? 'warning' : 'success');
      setNewKeyword('');
      setActiveAction(ACTION.NONE);
      setSearchInput(newKeyword.trim());
      await searchScrapers(newKeyword.trim(), partialMatch);
      onMutationComplete?.();
    } catch { /* error shown via hook */ }
  };

  const scrapers = searchResults?.scrapers || [];
  const hasSearched = searchResults !== null;
  const searchedKeyword = searchResults?.keyword || '';
  const allIds = scrapers.map((s) => s.keyword_id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
  const someSelected = allIds.some((id) => selectedIds.has(id));
  const selectedProjects = scrapers
    .filter((s) => selectedIds.has(s.keyword_id))
    .map((s) => s.project_name);

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>Search Scrapers</Typography>
          <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 0.5, mt: 1 }}>
            <TextField
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              fullWidth
              placeholder="Search for a keyword across all scrapers"
            />
            <Button
              variant="contained"
              startIcon={searchLoading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
              onClick={handleSearch}
              disabled={searchLoading || !searchInput.trim()}
              sx={{
                minWidth: 110,
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: '#5f94de',
                flexShrink: 0,
              }}
            >
              Search
            </Button>
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={partialMatch}
                onChange={(e) => setPartialMatch(e.target.checked)}
                size="small"
              />
            }
            label={<Typography variant="body2" color="text.secondary">Partial match (substring search)</Typography>}
            sx={{ mb: 2 }}
          />

          {hasSearched && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {scrapers.length > 0
                  ? `Found "${searchedKeyword}" in ${scrapers.length} scraper(s)`
                  : `No scrapers contain "${searchedKeyword}"`}
              </Typography>

              {scrapers.length > 0 && (
                <TableContainer
                  sx={{
                    maxHeight: 240,
                    overflow: 'auto',
                    mb: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={allSelected}
                            indeterminate={someSelected && !allSelected}
                            onChange={(e) => toggleSelectAll(allIds, e.target.checked)}
                            inputProps={{ 'aria-label': 'Select all scrapers' }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Scraper</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Keyword</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scrapers.map((s) => {
                        const checked = selectedIds.has(s.keyword_id);
                        return (
                          <TableRow
                            key={s.keyword_id}
                            hover
                            selected={checked}
                            onClick={() => toggleRowSelected(s.keyword_id)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={checked}
                                onChange={() => toggleRowSelected(s.keyword_id)}
                                onClick={(e) => e.stopPropagation()}
                                inputProps={{ 'aria-label': `Select ${s.project_name}` }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip label={s.project_name} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell>{s.keyword}</TableCell>
                            <TableCell>
                              {s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Button
                  variant={activeAction === ACTION.RENAME ? 'contained' : 'outlined'}
                  startIcon={<EditIcon />}
                  onClick={() => toggleAction(ACTION.RENAME)}
                  disabled={scrapers.length === 0}
                  size="small"
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                  color="info"
                >
                  Rename Keyword
                </Button>
                <Button
                  variant={activeAction === ACTION.DELETE_SELECTED ? 'contained' : 'outlined'}
                  startIcon={<DeleteIcon />}
                  onClick={() => toggleAction(ACTION.DELETE_SELECTED)}
                  disabled={selectedIds.size === 0}
                  size="small"
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                  color="error"
                >
                  {`Delete Selected${selectedIds.size ? ` (${selectedIds.size})` : ''}`}
                </Button>
                <Button
                  variant={activeAction === ACTION.DELETE_ALL ? 'contained' : 'outlined'}
                  startIcon={<DeleteIcon />}
                  onClick={() => toggleAction(ACTION.DELETE_ALL)}
                  disabled={scrapers.length === 0}
                  size="small"
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                  color="error"
                >
                  Delete from All
                </Button>
              </Box>

              <Collapse in={activeAction === ACTION.RENAME}>
                <Box
                  sx={{
                    p: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    backgroundColor: theme.palette.grey[50],
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                    Rename "{searchedKeyword}" across all {scrapers.length} scraper(s)
                  </Typography>
                  <TextField
                    label="New Keyword"
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    size="small"
                    fullWidth
                    placeholder="Enter the new keyword value"
                    sx={{ mb: 2 }}
                  />
                  <Button
                    variant="contained"
                    color="info"
                    onClick={handleMassEdit}
                    disabled={actionLoading || !newKeyword.trim() || newKeyword.trim() === searchInput.trim()}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    {actionLoading ? <CircularProgress size={20} /> : 'Rename Keyword'}
                  </Button>
                </Box>
              </Collapse>

              <Collapse in={activeAction === ACTION.DELETE_SELECTED}>
                <Box
                  sx={{
                    p: 2,
                    border: `1px solid ${theme.palette.error.light}`,
                    borderRadius: 1,
                    backgroundColor: theme.palette.error.lighter || '#fff5f5',
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Delete the {selectedIds.size} selected keyword(s)?
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    This will remove the keyword from:{' '}
                    {selectedProjects.join(', ')}
                  </Typography>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleDeleteSelected}
                    disabled={actionLoading || selectedIds.size === 0}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    {actionLoading ? <CircularProgress size={20} /> : 'Delete Selected'}
                  </Button>
                </Box>
              </Collapse>

              <Collapse in={activeAction === ACTION.DELETE_ALL}>
                <Box
                  sx={{
                    p: 2,
                    border: `1px solid ${theme.palette.error.light}`,
                    borderRadius: 1,
                    backgroundColor: theme.palette.error.lighter || '#fff5f5',
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Delete "{searchedKeyword}" from all {scrapers.length} scraper(s)?
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    This will remove the keyword from:{' '}
                    {scrapers.map((s) => s.project_name).join(', ')}
                  </Typography>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleDeleteAll}
                    disabled={actionLoading}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    {actionLoading ? <CircularProgress size={20} /> : 'Confirm Delete'}
                  </Button>
                </Box>
              </Collapse>
            </>
          )}

          {!hasSearched && (
            <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
              <SearchIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
              <Typography variant="body1">
                Search for a keyword to see which scrapers use it
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Then rename or delete it across multiple scrapers at once
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? 6000 : 4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
